/**
 * Read a `Response` body that may legitimately be absent (plan §2.2.2 · AD-10).
 *
 * `res.json()` throws on a body-less response, so any code path that calls it
 * unconditionally turns "204" or "200 with no body" into a thrown SyntaxError
 * — and whatever catch block is nearest then decides what that means, usually
 * wrongly. AD-10 answers it once: the status decides the outcome, and the body
 * is read separately and best-effort.
 *
 * This lives in its own module rather than in `auth-fetch.ts` because
 * `auth-fetch.ts` imports `authEndpoint` from `auth-context.tsx`, so
 * `auth-context.tsx` importing back would close a cycle — and `no-circular` is
 * an error, not a warning (.dependency-cruiser.cjs). Depending on nothing, it
 * is importable from both.
 */

/**
 * Ceiling on a body this module will materialize, in bytes.
 *
 * Every legitimate `/auth/*` payload is orders of magnitude under this: the
 * session envelope is a few dozen bytes, WebAuthn options roughly a kilobyte.
 * The server bounds what it ACCEPTS (`bodyLimit` 32 KB on `/auth/*`, security
 * §4); nothing bounds what a compromised upstream, a hijacked proxy or a
 * captive-portal splash page can SEND back, and `res.text()` would buffer all
 * of it into the tab before any status check runs.
 */
const MAX_BODY_BYTES = 64 * 1024;

/**
 * Read at most `MAX_BODY_BYTES`, then stop pulling.
 *
 * The read is incremental precisely so an oversized body is abandoned rather
 * than materialized and discarded — `res.text()` has already cost the memory by
 * the time its length can be measured. `cancel()` releases the connection
 * instead of draining the remainder.
 */
async function readBounded(body: ReadableStream<Uint8Array>): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let text = '';
  let bytes = 0;
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) return text + decoder.decode();
    bytes += chunk.value.byteLength;
    if (bytes > MAX_BODY_BYTES) {
      await reader.cancel();
      return '';
    }
    /* `stream: true` keeps a multi-byte character split across a chunk
       boundary from decoding as two replacement characters. */
    text += decoder.decode(chunk.value, { stream: true });
  }
}

/**
 * Never throws: an unreadable body is an absent body, not a failure. An
 * OVERSIZED body is also an absent body — the same outcome as unparseable,
 * because there is no caller that could do anything useful with a truncation.
 */
async function readText(res: Response): Promise<string> {
  try {
    /* A declared oversize is rejected without reading a byte. The header is a
       hint a hostile upstream controls, so the streaming cap still runs
       underneath it rather than trusting the number. */
    const declared = declaredLength(res);
    if (declared !== null && declared > MAX_BODY_BYTES) return '';
    const stream = bodyStream(res);
    if (stream === null) return await res.text();
    return await readBounded(stream);
  } catch {
    return '';
  }
}

/**
 * `content-length` as a number, or null when absent, unparseable, or when the
 * response exposes no `headers` at all.
 *
 * A missing header must not abort the read — it is routine (chunked transfer)
 * and it is what a partial `Response` double provides. Only an explicit,
 * parseable oversize is actionable here.
 */
function declaredLength(res: Response): number | null {
  try {
    const raw = res.headers.get('content-length');
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The body as a readable stream, or null when this response cannot supply one.
 *
 * Null on a genuinely body-less response (204), and on any `Response`-like that
 * exposes no stream — a partial test double, or a polyfill predating
 * `Response.body`. Those fall back to `text()`, which is unbounded; the ceiling
 * can only be enforced where the read can actually be interrupted. That is a
 * real limit of the fallback, not an oversight: every such case in this
 * codebase is a fixture whose size the test itself chose.
 */
function bodyStream(res: Response): ReadableStream<Uint8Array> | null {
  const body: unknown = res.body;
  if (body === null || body === undefined) return null;
  const candidate = body as Partial<ReadableStream<Uint8Array>>;
  return typeof candidate.getReader === 'function' ? (body as ReadableStream<Uint8Array>) : null;
}

/**
 * The parsed body, or `undefined` when there is none and when there is one that
 * is not JSON. Status-agnostic: empty, unreadable, and non-JSON are an absent
 * body. Callers combine `res.ok` / `res.status` with the parsed value.
 *
 * `undefined` means absent; it does not mean falsy. A body of the four bytes
 * `null` is VALID JSON and resolves as `null`, not `undefined` — so a caller
 * that means "no body" must test `=== undefined`, never truthiness, or it will
 * conflate an absent body with a present `null`, `0`, `""` or `false`. Both
 * current callers narrow with `isRecord`, which rejects every one of those.
 */
export async function readOptionalJson(res: Response): Promise<unknown> {
  const text = await readText(res);
  if (text.length === 0) return undefined;
  try {
    const value: unknown = JSON.parse(text);
    return value;
  } catch {
    return undefined;
  }
}
